import { debug } from '../utils/debug';
import { calculateApplicationFee } from '../utils/platformFee';

// PI creation/update fires eagerly (StripePaymentElement mounts before the
// form is fully filled, and iOS Safari can re-mount it with stale userInfo).
// Strip any non-email-shaped value so we never ship a partial address to the
// backend, which would forward it to Stripe and get email_invalid back.
const looksLikeEmail = (value) =>
  typeof value === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
const sanitizeReceiptEmail = (value) => (looksLikeEmail(value) ? value : null);

// Real Stripe configuration
export const STRIPE_CONFIG = {
  // Your platform's publishable key from Stripe Dashboard
  publishableKey: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 'pk_test_your_actual_publishable_key_here',

  // API base URL for your backend
  // Use relative URL to ensure it works with Vercel preview deployments
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL || '/api',
  
  // Merchant-specific configuration
  merchants: {
    rideshare: {
      // Use environment variable if available, otherwise placeholder
      connectedAccountId: import.meta.env.VITE_STRIPE_RIDESHARE_ACCOUNT_ID || 'acct_your_rideshare_connected_account_id',
      platformFeePercentage: 0.02, // 2%
      deposits: {
        enabled: true,
        rules: [
          { category: 'Electric Bikes', amount: 100, currency: 'USD' },
          { category: '*', amount: 50, currency: 'USD' }
        ]
      },
      offSessionPayments: false
    },
    cyclecentral: {
      // Use environment variable if available, otherwise placeholder
      connectedAccountId: import.meta.env.VITE_STRIPE_CYCLECENTRAL_ACCOUNT_ID || 'acct_your_cyclecentral_connected_account_id',
      platformFeePercentage: 0.03, // 3%
      deposits: {
        enabled: false,
        rules: []
      },
      offSessionPayments: true
    }
  }
};

// Helper function to get merchant Stripe config
export const getMerchantStripeConfig = (merchantId) => {
  return STRIPE_CONFIG.merchants[merchantId] || STRIPE_CONFIG.merchants.rideshare;
};

// Calculate deposit amount based on selected products
// depositRules: optional array from DB merchants.deposit_rules [{amount (cents), category_slug}]
export const calculateDepositAmount = (merchantId, selectedProducts, depositRules = null) => {
  // If DB deposit rules are provided, use them (amounts are in cents)
  if (depositRules && depositRules.length > 0) {
    let totalDeposit = 0;
    selectedProducts.forEach(product => {
      const quantity = product.quantity;
      const slug = product.category_slug || '';
      // Match by category_slug, fall back to wildcard '*'
      const rule = depositRules.find(r => r.category_slug === slug) ||
                   depositRules.find(r => r.category_slug === '*');
      if (rule) {
        totalDeposit += (rule.amount / 100) * quantity; // Convert cents to currency units
      }
    });
    return totalDeposit;
  }

  // Fallback: hardcoded config (legacy)
  const config = getMerchantStripeConfig(merchantId);

  if (!config.deposits.enabled) {
    return 0;
  }

  let totalDeposit = 0;
  selectedProducts.forEach(product => {
    const quantity = product.quantity;

    // Find matching deposit rule
    const rule = config.deposits.rules.find(r =>
      r.category === product.category || r.category === '*'
    );

    if (rule) {
      totalDeposit += rule.amount * quantity;
    }
  });

  return totalDeposit;
};

// Calculate platform fee (location-level override takes precedence).
// Additive model mirrors Stripe: pct (whole-number %) * total + fixedCents.
//
// Args:
//   - amount: charge total in currency units (e.g. 50.00 EUR)
//   - locationFeeOverride: location % override (whole number, e.g. 2 = 2%) or null
//   - locationFixedOverride: location fixed override in smallest unit or null
//   - merchantFixedCents: merchant default fixed component in smallest unit
//
// Returns: fee in currency units (decimal, rounded to 2dp).
//
// Note: locationFeeOverride uses DB convention (whole-number %). Hardcoded
// merchant config uses decimal (0.02 = 2%) — normalized internally.
export const calculatePlatformFee = (
  merchantId,
  amount,
  locationFeeOverride = null,
  locationFixedOverride = null,
  merchantFixedCents = null
) => {
  const config = getMerchantStripeConfig(merchantId);
  // Resolve pct as whole number (DB convention). Hardcoded config uses decimal,
  // so multiply by 100 to convert.
  const pct = locationFeeOverride != null
    ? locationFeeOverride
    : (config.platformFeePercentage || 0) * 100;
  const fixedCents = locationFixedOverride != null
    ? locationFixedOverride
    : (merchantFixedCents || 0);
  const totalCents = Math.round(amount * 100);
  const feeCents = calculateApplicationFee(totalCents, pct, fixedCents);
  return feeCents / 100;
};

// API helper functions with better error handling
// merchantConfig: optional object from database with { merchant_type, stripe_connected_account_id, deposits_enabled, platform_fee_percentage }
// options: optional { paymentMethodTypesOverride, allowCardFallback } for the
//   bucket UI — pass e.g. { paymentMethodTypesOverride: ['mobilepay'],
//   allowCardFallback: false } to create a MobilePay-isolated PI. The backend
//   applies the per-method surcharge and keeps card out of that bucket.
export const createPaymentIntent = async (merchantId, bookingData, totalAmount, merchantCurrency = 'usd', offSessionPaymentsOverride = null, merchantConfig = null, customerEmail = null, options = {}) => {
  debug.log('Creating payment intent...', {
    merchantId,
    totalAmount,
    currency: merchantCurrency,
    apiUrl: STRIPE_CONFIG.apiBaseUrl,
    merchantConfig: merchantConfig ? {
      merchant_type: merchantConfig.merchant_type,
      has_connected_account: Boolean(merchantConfig.stripe_connected_account_id),
      deposits_enabled: merchantConfig.deposits_enabled
    } : 'using hardcoded config'
  });

  const hardcodedConfig = getMerchantStripeConfig(merchantId);

  // Determine connected account ID:
  // - Platform merchants (merchant_type = 'platform') should NOT use connected accounts
  // - Partner merchants use stripe_connected_account_id from database if available
  let connectedAccountId = null;
  if (merchantConfig) {
    // Use database values
    if (merchantConfig.merchant_type === 'partner' && merchantConfig.stripe_connected_account_id) {
      connectedAccountId = merchantConfig.stripe_connected_account_id;
    }
    // For 'platform' type, connectedAccountId stays null
  } else {
    // Fall back to hardcoded config (legacy behavior)
    connectedAccountId = hardcodedConfig.connectedAccountId;
  }

  // Determine if deposits are enabled
  const depositsEnabled = merchantConfig?.deposits_enabled ?? hardcodedConfig.deposits?.enabled ?? false;

  // Calculate deposit amount only if deposits are enabled
  // Pass DB deposit_rules for correct per-category amounts
  const depositRules = merchantConfig?.deposit_rules || null;
  const depositAmount = depositsEnabled ? calculateDepositAmount(merchantId, bookingData.selectedProducts, depositRules) : 0;

  // Calculate platform fee (only for partner merchants with connected accounts).
  // Additive model: pct (whole-number %) * total + fixedCents. Location override
  // wins (for BOTH pct and fixed) when non-null; else merchant default; else
  // hardcoded fallback (legacy pct only, fixed = 0).
  //
  // Percentage units:
  //   - DB stores whole number (e.g. 2 = 2%)
  //   - Hardcoded config stores decimal (e.g. 0.02 for 2%)
  //   Normalize everything to whole-number % for the shared helper.
  const locationFee = bookingData?.location?._apiData?.platform_fee_percentage;
  const locationFixed = bookingData?.location?._apiData?.platform_fee_fixed_cents;
  const platformFeePctWhole = locationFee != null
    ? locationFee
    : merchantConfig?.platform_fee_percentage != null
      ? merchantConfig.platform_fee_percentage
      : (hardcodedConfig.platformFeePercentage ?? 0.02) * 100;
  const platformFeeFixedCents = locationFixed != null
    ? locationFixed
    : (merchantConfig?.platform_fee_fixed_cents || 0);
  const platformFeeCents = connectedAccountId
    ? calculateApplicationFee(Math.round(totalAmount * 100), platformFeePctWhole, platformFeeFixedCents)
    : 0;
  const platformFee = platformFeeCents / 100;

  // Use database value if provided, otherwise fall back to config value
  const offSessionPayments = offSessionPaymentsOverride !== null ? offSessionPaymentsOverride : hardcodedConfig.offSessionPayments;

  // Convert currency code to lowercase (Stripe requires lowercase)
  const currencyCode = (merchantCurrency || 'usd').toLowerCase();

  // Method list: a bucket override (e.g. ['mobilepay']) wins; otherwise pass the
  // merchant's configured payment_method_types through unchanged. The backend
  // (api/_utils/paymentMethods.js → filterPaymentMethods) is the authoritative
  // filter and applies per-method currency / off-session rules + surcharge.
  const { paymentMethodTypesOverride = null, allowCardFallback = true } = options;
  const paymentMethods = paymentMethodTypesOverride && paymentMethodTypesOverride.length > 0
    ? paymentMethodTypesOverride
    : (merchantConfig?.payment_method_types && merchantConfig.payment_method_types.length > 0
        ? merchantConfig.payment_method_types
        : ['card']);

  debug.log('Payment intent parameters:', {
    connectedAccountId: connectedAccountId || 'none (platform merchant)',
    depositsEnabled,
    depositAmount,
    platformFee,
    offSessionPayments,
    paymentMethods,
    allowCardFallback
  });

  try {
    const response = await fetch(`${STRIPE_CONFIG.apiBaseUrl}/create-payment-intent`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        amount: Math.round(totalAmount * 100), // Convert to cents
        currency: currencyCode,
        merchantId,
        connectedAccountId: connectedAccountId, // null for platform merchants
        platformFee: platformFeeCents, // Already in cents from calculateApplicationFee (0 for platform merchants)
        depositAmount: Math.round(depositAmount * 100), // Convert to cents (0 if deposits disabled)
        bookingData,
        offSessionPayments: offSessionPayments,
        paymentMethodTypes: paymentMethods, // From merchant_config.payment_method_types or bucket override; backend filters per METHOD_RULES
        allowCardFallback, // false for bucket-isolated PIs (e.g. MobilePay) so card isn't injected
        customerEmail: sanitizeReceiptEmail(customerEmail) // Customer email for Stripe receipt; only sent when format-valid
      }),
    });

    debug.log('API Response status:', response.status);

    if (!response.ok) {
      // Try to parse error response as JSON first
      let errorMessage = `API Error (${response.status})`;
      try {
        const errorData = await response.json();
        // Use the message field if available, otherwise use error field
        errorMessage = errorData.message || errorData.error || errorMessage;
        console.error('API Error Response:', errorData);
      } catch (e) {
        // If JSON parsing fails, fall back to text
        const errorText = await response.text();
        errorMessage = errorText || errorMessage;
        console.error('API Error Response (text):', errorText);
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    debug.log('Payment intent created successfully:', data);
    return data;

  } catch (error) {
    console.error('Error creating payment intent:', error);
    
    // More specific error messages
    if (error.message.includes('fetch') || error.name === 'TypeError') {
      throw new Error('Unable to connect to payment server. Please check your internet connection.');
    }
    
    throw new Error(`Payment initialization failed: ${error.message}`);
  }
};

// Update an existing payment intent (e.g. when coupon is applied/removed after creation).
//
// locationFeeOverride / locationFixedOverride: optional per-location overrides.
// Merchant-level fixed component is read from merchantConfig.platform_fee_fixed_cents.
export const updatePaymentIntent = async (
  paymentIntentId,
  totalAmount,
  merchantId,
  merchantConfig = null,
  customerEmail = null,
  locationFeeOverride = null,
  locationFixedOverride = null,
  options = {}
) => {
  const hardcodedConfig = getMerchantStripeConfig(merchantId);

  // Determine connected account ID (same logic as createPaymentIntent)
  let connectedAccountId = null;
  if (merchantConfig) {
    if (merchantConfig.merchant_type === 'partner' && merchantConfig.stripe_connected_account_id) {
      connectedAccountId = merchantConfig.stripe_connected_account_id;
    }
  } else {
    connectedAccountId = hardcodedConfig.connectedAccountId;
  }

  // Calculate additive platform fee. Percentage units normalized to whole number
  // for the shared helper (DB convention).
  const platformFeePctWhole = locationFeeOverride != null
    ? locationFeeOverride
    : merchantConfig?.platform_fee_percentage != null
      ? merchantConfig.platform_fee_percentage
      : (hardcodedConfig.platformFeePercentage ?? 0.02) * 100;
  const platformFeeFixedCents = locationFixedOverride != null
    ? locationFixedOverride
    : (merchantConfig?.platform_fee_fixed_cents || 0);
  const platformFeeCents = connectedAccountId
    ? calculateApplicationFee(Math.round(totalAmount * 100), platformFeePctWhole, platformFeeFixedCents)
    : 0;
  const platformFee = platformFeeCents / 100;

  debug.log('🔄 [STRIPE] Updating payment intent:', {
    paymentIntentId,
    newAmount: totalAmount,
    platformFee,
    connectedAccountId: connectedAccountId || 'none'
  });

  // Bucket switch: pass the new method list + currency so the backend updates
  // payment_method_types and re-applies the per-method surcharge. Omitted for
  // plain amount/coupon updates (backend then preserves the PI's current
  // methods and surcharge).
  const {
    paymentMethodTypesOverride = null,
    allowCardFallback = true,
    currency = null
  } = options;

  try {
    const response = await fetch(`${STRIPE_CONFIG.apiBaseUrl}/update-payment-intent`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        paymentIntentId,
        amount: Math.round(totalAmount * 100), // Convert to cents
        platformFee: connectedAccountId ? platformFeeCents : null, // Already in cents; null for platform merchants
        ...(paymentMethodTypesOverride && paymentMethodTypesOverride.length > 0
          ? { paymentMethodTypes: paymentMethodTypesOverride, allowCardFallback, currency: (currency || '').toLowerCase() || null }
          : {}),
        customerEmail: sanitizeReceiptEmail(customerEmail)
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Network error' }));
      throw new Error(errorData.error || `API Error (${response.status})`);
    }

    const data = await response.json();
    debug.log('✅ [STRIPE] Payment intent updated successfully:', data);
    return data;

  } catch (error) {
    console.error('❌ [STRIPE] Error updating payment intent:', error);
    throw new Error(`Payment update failed: ${error.message}`);
  }
};

// Create a hosted Stripe Checkout Session as a fallback when the embedded
// Payment Element can't be completed (iOS Safari checkout reloads, repeated
// card-entry failures). Mirrors createPaymentIntent's connected-account / fee /
// off-session resolution, then posts to /create-fallback-checkout-session, which
// also cancels the embedded PaymentIntent (passed as embeddedPaymentIntentId) so
// only one PaymentIntent is ever payable.
//
// Params:
//   bookingData       UI booking data (used for location-level fee overrides)
//   savedBookingData  canonical pending_bookings shape (sent to the backend)
//   embeddedPaymentIntentId  the current embedded PI to cancel (may be empty)
// Returns the backend payload, e.g. { url } or { alreadyPaid: true }.
export const createFallbackCheckoutSession = async ({
  merchantId,
  bookingData,
  savedBookingData,
  totalAmount,
  currency = 'usd',
  offSessionPayments = false,
  merchantConfig = null,
  customerEmail = null,
  embeddedPaymentIntentId = null,
  locale = null,
  origin = null,
  cancelUrl = null,
}) => {
  const hardcodedConfig = getMerchantStripeConfig(merchantId);

  // Connected account (same rule as createPaymentIntent).
  let connectedAccountId = null;
  if (merchantConfig) {
    if (merchantConfig.merchant_type === 'partner' && merchantConfig.stripe_connected_account_id) {
      connectedAccountId = merchantConfig.stripe_connected_account_id;
    }
  } else {
    connectedAccountId = hardcodedConfig.connectedAccountId;
  }

  // Deposit amount (only when deposits enabled).
  const depositsEnabled = merchantConfig?.deposits_enabled ?? hardcodedConfig.deposits?.enabled ?? false;
  const depositRules = merchantConfig?.deposit_rules || null;
  const depositAmount = depositsEnabled
    ? calculateDepositAmount(merchantId, bookingData.selectedProducts, depositRules)
    : 0;

  // Additive platform fee (location override wins), in cents.
  const locationFee = bookingData?.location?._apiData?.platform_fee_percentage;
  const locationFixed = bookingData?.location?._apiData?.platform_fee_fixed_cents;
  const platformFeePctWhole = locationFee != null
    ? locationFee
    : merchantConfig?.platform_fee_percentage != null
      ? merchantConfig.platform_fee_percentage
      : (hardcodedConfig.platformFeePercentage ?? 0.02) * 100;
  const platformFeeFixedCents = locationFixed != null
    ? locationFixed
    : (merchantConfig?.platform_fee_fixed_cents || 0);
  const platformFeeCents = connectedAccountId
    ? calculateApplicationFee(Math.round(totalAmount * 100), platformFeePctWhole, platformFeeFixedCents)
    : 0;

  const locationId = bookingData.location?._apiData?.id || bookingData.location?.id;

  const response = await fetch(`${STRIPE_CONFIG.apiBaseUrl}/create-fallback-checkout-session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      amount: Math.round(totalAmount * 100),
      currency: (currency || 'usd').toLowerCase(),
      merchantId,
      connectedAccountId,
      platformFee: connectedAccountId ? platformFeeCents : 0,
      depositAmount: Math.round(depositAmount * 100),
      offSessionPayments,
      customerEmail: sanitizeReceiptEmail(customerEmail),
      bookingData: savedBookingData,
      locationId,
      locale,
      origin,
      cancelUrl,
      embeddedPaymentIntentId,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Network error' }));
    // Include server-provided details so the real cause (e.g. a Postgres error
    // on the pending_bookings write) is visible in the console, not just the
    // generic top-line message.
    const base = errorData.message || errorData.error || `API Error (${response.status})`;
    throw new Error(errorData.details ? `${base}: ${errorData.details}` : base);
  }

  return response.json();
};

export const confirmPayment = async (paymentIntentId, paymentMethodId) => {
  const response = await fetch(`${STRIPE_CONFIG.apiBaseUrl}/confirm-payment`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      paymentIntentId,
      paymentMethodId
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to confirm payment');
  }

  return response.json();
};
